import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";

/**
 * LockedCampsiteOverlay — iter189
 *
 * Renders a full-tile lock overlay over children (used to hide the map,
 * price panel and facilities on the CampsiteDetail page for anonymous
 * visitors). Signed-in visitors bypass this at the caller.
 *
 * Usage:
 *   <LockedCampsiteOverlay label="Prices">
 *     <PricePanel />
 *   </LockedCampsiteOverlay>
 */
export default function LockedCampsiteOverlay({ label = "This section", children }) {
  const location = useLocation();
  const next = location.pathname + location.search;
  const href = `/register?next=${encodeURIComponent(next)}`;

  return (
    <div className="relative" data-testid={`locked-overlay-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {/* Blurred children — kept in the DOM so screen-readers still see
          the structure and the layout doesn't collapse. */}
      <div aria-hidden="true" className="pointer-events-none select-none filter blur-md opacity-60">
        {children}
      </div>
      {/* Lock CTA on top */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-sm bg-white border-2 border-[#0e0e0e] shadow-[0_6px_0_0_#111] p-4 sm:p-5 text-center">
          <div className="inline-flex items-center gap-1.5 bg-[#fff4ec] text-[#ff4500] px-2 py-0.5 text-[10px] mono uppercase tracking-widest font-black mb-2">
            <Lock className="w-3 h-3" /> Members only
          </div>
          <h3 className="font-display text-lg sm:text-xl mb-1 leading-tight">
            {label} unlock in one tap
          </h3>
          <p className="text-xs text-[#555] leading-snug mb-3">
            Free while we're growing. Sign up in 60 seconds — no card, no catch.
          </p>
          <Link
            to={href}
            className="inline-flex items-center justify-center gap-1.5 bg-[#ff4500] text-white font-black text-xs uppercase tracking-widest px-4 py-2.5 shadow-[0_4px_0_0_#111] hover:translate-y-[1px] hover:shadow-[0_3px_0_0_#111] transition-transform"
            data-testid="locked-overlay-cta"
          >
            Sign up free <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
